import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAdminSupport } from "../../hooks/use-admin.js";
import { PageSkeleton } from "../../components/skeleton.js";
import type { SupportAlert } from "../../api/admin-summaries.js";

const INITIAL_LIMIT = 20;

function AlertTypeBadge({ type }: { type: SupportAlert["type"] }) {
  const label = type.replace(/_/g, " ");
  // Map alert types to appropriate badge variants
  const variant =
    type === "failed_run" || type === "waf_blocking"
      ? "badge--fail"
      : type === "low_confidence" || type === "pac_unavailable"
        ? "badge--warning"
        : "badge--advisory";
  return <span className={`badge ${variant}`}>{label}</span>;
}

function AlertItem({ alert }: { alert: SupportAlert }) {
  return (
    <div className="admin-alert-item">
      <div className="d-flex items-center gap-2 flex-wrap">
        <Link
          to={`/admin/websites/${alert.websiteId}`}
          className="fw-600 text-sm"
        >
          {alert.websiteUrl}
        </Link>
        <AlertTypeBadge type={alert.type} />
      </div>
      <p
        className={`text-sm mt-1 ${alert.severity === "critical" ? "text-danger" : "text-warning"}`}
      >
        {alert.message}
      </p>
    </div>
  );
}

export function AdminSupportPage() {
  const { data, isLoading, error } = useAdminSupport();
  const queryClient = useQueryClient();
  const [showAllWarnings, setShowAllWarnings] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  async function handleRefresh() {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ["admin-support"] });
    setIsRefreshing(false);
  }

  if (isLoading) return <PageSkeleton />;

  if (error) {
    return (
      <div className="product-page">
        <h1 className="page-title">Support / Ops</h1>
        <section className="product-section">
          <div className="alert alert--error">
            Failed to load support data.{" "}
            {error instanceof Error ? error.message : ""}
          </div>
        </section>
      </div>
    );
  }

  // Safely normalise alerts
  const alerts: SupportAlert[] = Array.isArray(data?.alerts)
    ? data.alerts
    : [];

  const criticalAlerts = alerts.filter((a) => a.severity === "critical");
  const warningAlerts = alerts.filter((a) => a.severity === "warning");
  const visibleWarnings =
    showAllWarnings || warningAlerts.length <= INITIAL_LIMIT
      ? warningAlerts
      : warningAlerts.slice(0, INITIAL_LIMIT);
  const hasMoreWarnings =
    !showAllWarnings && warningAlerts.length > INITIAL_LIMIT;

  return (
    <div className="product-page">
      <div className="d-flex items-center justify-between mb-3">
        <h1 className="page-title">Support / Ops</h1>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => void handleRefresh()}
          disabled={isRefreshing}
        >
          {isRefreshing ? "Refreshing\u2026" : "Refresh"}
        </button>
      </div>

      {criticalAlerts.length > 0 && (
        <section
          className="product-section admin-alert-critical"
          aria-labelledby="critical-heading"
        >
          <h2 id="critical-heading" className="section-title">
            Critical Alerts ({criticalAlerts.length})
          </h2>
          {criticalAlerts.map((alert, i) => (
            <AlertItem key={i} alert={alert} />
          ))}
        </section>
      )}

      {warningAlerts.length > 0 && (
        <section
          className="product-section admin-alert-warning"
          aria-labelledby="warning-heading"
        >
          <h2 id="warning-heading" className="section-title">
            Warnings ({warningAlerts.length})
          </h2>
          {visibleWarnings.map((alert, i) => (
            <AlertItem key={i} alert={alert} />
          ))}
          {hasMoreWarnings && (
            <button
              type="button"
              className="btn btn--secondary btn--sm mt-2"
              onClick={() => setShowAllWarnings(true)}
            >
              Show all {warningAlerts.length} warnings
            </button>
          )}
        </section>
      )}

      {alerts.length === 0 && (
        <section className="product-section">
          <p className="text-success">All systems healthy</p>
        </section>
      )}
    </div>
  );
}
